<template>
  <Card class="activity-card">
    <div slot="title" class="title-row">
      <span><Icon type="ios-flame" color="#ff6b35" /> 灶火心法卡池</span>
      <Button size="small" type="text" icon="md-refresh" :loading="loading" @click="refresh">刷新</Button>
    </div>

    <Alert type="warning" show-icon>
      固定材料十连抽，抽取结果立即发放。第10次及其倍数保底绝品，第50次及其倍数保底灵宝。
    </Alert>

    <div class="draw-layout">
      <div class="draw-panel">
        <h3>选择心法范围</h3>
        <RadioGroup v-model="attributeScope" type="button" class="scope-group">
          <Radio label="FORCE"><Icon type="ios-flash" /> 力道</Radio>
          <Radio label="SPIRIT"><Icon type="ios-water" /> 灵力</Radio>
        </RadioGroup>

        <div class="material-box">
          <div class="material-icon"><Icon type="ios-diamond" /></div>
          <div>
            <strong>{{ materialName }}</strong>
            <p>拥有 {{ materialCount }} 个，每次十连消耗 {{ materialCost }} 个</p>
          </div>
        </div>

        <Button type="primary" size="large" long :loading="drawing" :disabled="!canDraw" @click="draw">
          <Icon type="ios-flame" /> 立即十连抽
        </Button>
        <p v-if="!canDraw" class="empty-hint">材料不足，无法抽取</p>
      </div>

      <div class="pity-panel">
        <h3>卡池进度</h3>
        <div class="pity-row">
          <span>累计抽取</span><strong>{{ totalDrawCount }}</strong>
        </div>
        <div class="pity-row">
          <span>距绝品保底</span><strong>{{ drawsToJuepin }} 抽</strong>
        </div>
        <div class="pity-row">
          <span>距灵宝保底</span><strong>{{ drawsToLingbao }} 抽</strong>
        </div>
        <Divider />
        <p class="scope-note">当前范围：{{ scopeName }}</p>
        <p class="scope-note">生成的属性和技能强化会严格匹配所选范围。</p>
      </div>
    </div>

    <Divider>最近抽取</Divider>
    <div v-if="history.length" class="history-list">
      <div v-for="record in history" :key="record.drawId" class="history-item">
        <div>
          <strong>{{ record.attributeScope === 'FORCE' ? '力道' : '灵力' }}十连</strong>
          <span class="history-time">{{ record.createTime }}</span>
        </div>
        <div class="history-summary">
          第 {{ record.drawCountStart }} - {{ record.drawCountEnd }} 抽
          <Tag v-for="reward in parseRewards(record.rewardJson)" :key="reward.instanceId" :color="rewardColor(reward.quality)">
            {{ reward.qualityName }} {{ reward.xinfaName }}
          </Tag>
        </div>
      </div>
    </div>
    <div v-else class="empty-history">暂无抽取记录</div>

    <Modal
      v-model="showResult"
      title="十连抽取结果"
      width="760"
      :mask-closable="false"
      :closable="revealPhase === 'complete'"
      class-name="hearth-result-modal"
      @on-visible-change="handleResultVisibleChange"
    >
      <div class="reveal-stage" :class="['phase-' + revealPhase, 'flash-quality-' + rareFlashQuality]">
        <div v-if="rareFlashQuality" class="rare-flash"></div>

        <div v-if="revealPhase === 'charging'" class="charging-scene">
          <div class="hearth-rings"><i></i><i></i><i></i></div>
          <div class="hearth-core"><Icon type="ios-flame" /></div>
          <strong>灶火凝诀</strong>
          <span>{{ resultScopeName }}灵蕴正在汇聚</span>
        </div>

        <template v-else>
          <div class="result-head">
            <span>{{ resultScopeName }}心法</span>
            <strong v-if="revealPhase === 'revealing'">正在揭示 {{ revealedCount }} / {{ resultRewards.length }}</strong>
            <span v-else>第 {{ result.drawCountStart }} - {{ result.drawCountEnd }} 抽</span>
          </div>
          <div class="result-grid">
            <div
              v-for="(reward, index) in resultRewards"
              :key="reward.instanceId || index"
              class="result-card-shell"
              :class="{ revealed: index < revealedCount }"
            >
              <div class="result-card">
                <div class="result-card-back">
                  <Icon type="ios-flame" />
                  <span>永诀</span>
                </div>
                <div class="result-card-front" :class="'quality-' + Number(reward.quality || 1)">
                  <div class="quality-aura"></div>
                  <Tag :color="rewardColor(reward.quality)">{{ reward.qualityName }}</Tag>
                  <strong>{{ reward.xinfaName }}</strong>
                  <span>{{ reward.attributeScope === 'FORCE' ? '力道' : '灵力' }} · {{ reward.maxLayer || 0 }}层</span>
                  <small>已收入野外心法</small>
                </div>
              </div>
            </div>
          </div>
          <div v-if="revealPhase === 'complete'" class="result-complete">
            <Icon type="md-checkmark-circle" /> 十本心法已全部生成并收入心法库
          </div>
        </template>
      </div>
      <div slot="footer" class="result-footer">
        <Button v-if="revealPhase !== 'complete'" type="text" icon="md-skip-forward" @click="skipReveal">跳过动画</Button>
        <Button v-else type="primary" icon="md-checkmark" @click="showResult = false">确定</Button>
      </div>
    </Modal>
  </Card>
</template>

<script>
export default {
  name: 'hearthActivity',
  data () {
    return {
      attributeScope: 'FORCE',
      materialId: '42045',
      materialName: '上品玄霜晶核',
      materialCount: 0,
      materialCost: 10,
      totalDrawCount: 0,
      drawsToJuepin: 10,
      drawsToLingbao: 50,
      history: [],
      result: { rewards: [] },
      showResult: false,
      revealPhase: 'idle',
      revealedCount: 0,
      rareFlashQuality: 0,
      revealTimers: [],
      loading: false,
      drawing: false
    }
  },
  computed: {
    canDraw () {
      return this.materialCount >= this.materialCost && !this.drawing
    },
    scopeName () {
      return this.attributeScope === 'FORCE' ? '力道' : '灵力'
    },
    resultScopeName () {
      return this.result.attributeScope === 'FORCE' ? '力道' : '灵力'
    },
    resultRewards () {
      return Array.isArray(this.result.rewards) ? this.result.rewards : []
    }
  },
  methods: {
    charaId () {
      const value = `; ${document.cookie}`
      const parts = value.split('; charaId=')
      return parts.length === 2 ? parts.pop().split(';').shift() : ''
    },
    async refresh () {
      this.loading = true
      try {
        const [status, history] = await Promise.all([
          this.$http.get('/gameActivity/getHearthStatus', { params: { charaId: this.charaId() } }),
          this.$http.get('/gameActivity/getHearthHistory', { params: { charaId: this.charaId() } })
        ])
        if (status.data.status === 200) {
          const data = status.data.data || {}
          this.materialId = data.materialItemId || this.materialId
          this.materialName = data.materialItemName || this.materialName
          this.materialCount = data.materialCount || 0
          this.materialCost = (data.materialPerPull || 1) * (data.pullsPerRequest || 10)
          this.totalDrawCount = data.totalDrawCount || 0
          this.drawsToJuepin = data.drawsToJuepin || 10
          this.drawsToLingbao = data.drawsToLingbao || 50
        }
        if (history.data.status === 200) this.history = history.data.data || []
      } catch (error) {
        this.$Message.error('灶火数据刷新失败')
      } finally {
        this.loading = false
      }
    },
    async draw () {
      if (!this.canDraw) return
      this.drawing = true
      try {
        const response = await this.$http.post('/gameActivity/drawWildXinfa', {
          charaId: this.charaId(),
          drawType: 'TEN',
          attributeScope: this.attributeScope
        })
        if (response.data.status !== 200) {
          this.$Message.error(response.data.msg || '抽取失败')
          return
        }
        this.result = response.data.data || { rewards: [] }
        this.showResult = true
        this.startResultReveal()
        await this.refresh()
      } catch (error) {
        this.$Message.error('抽取失败')
      } finally {
        this.drawing = false
      }
    },
    parseRewards (json) {
      if (!json) return []
      if (Array.isArray(json)) return json
      try { return JSON.parse(json) || [] } catch (error) { return [] }
    },
    rewardColor (quality) {
      return { 1: 'blue', 2: 'purple', 3: 'gold' }[quality] || 'default'
    },
    startResultReveal () {
      this.clearRevealTimers()
      this.revealedCount = 0
      this.rareFlashQuality = 0
      this.revealPhase = 'charging'
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      this.queueRevealTimer(() => {
        if (reduceMotion) {
          this.skipReveal()
          return
        }
        this.revealPhase = 'revealing'
        this.queueRevealTimer(this.revealNext, 180)
      }, reduceMotion ? 80 : 1100)
    },
    revealNext () {
      if (!this.showResult || this.revealPhase !== 'revealing') return
      if (this.revealedCount >= this.resultRewards.length) {
        this.revealPhase = 'complete'
        return
      }
      const reward = this.resultRewards[this.revealedCount] || {}
      const quality = Number(reward.quality || 1)
      this.revealedCount += 1
      if (quality >= 2) {
        this.rareFlashQuality = quality
        this.queueRevealTimer(() => { this.rareFlashQuality = 0 }, quality === 3 ? 720 : 430)
      }
      if (this.revealedCount >= this.resultRewards.length) {
        this.queueRevealTimer(() => { this.revealPhase = 'complete' }, quality === 3 ? 760 : 420)
        return
      }
      const delay = quality === 3 ? 820 : (quality === 2 ? 520 : 230)
      this.queueRevealTimer(this.revealNext, delay)
    },
    skipReveal () {
      this.clearRevealTimers()
      this.revealedCount = this.resultRewards.length
      this.rareFlashQuality = 0
      this.revealPhase = 'complete'
    },
    handleResultVisibleChange (visible) {
      if (!visible) {
        this.clearRevealTimers()
        this.revealPhase = 'idle'
        this.rareFlashQuality = 0
      }
    },
    queueRevealTimer (callback, delay) {
      const timer = window.setTimeout(callback, delay)
      this.revealTimers.push(timer)
      return timer
    },
    clearRevealTimers () {
      this.revealTimers.forEach(timer => window.clearTimeout(timer))
      this.revealTimers = []
    }
  },
  mounted () {
    this.refresh()
  },
  beforeDestroy () {
    this.clearRevealTimers()
  }
}
</script>

<style scoped>
.activity-card { margin: 20px; }
.title-row { display: flex; align-items: center; justify-content: space-between; }
.draw-layout { display: flex; gap: 32px; margin-top: 24px; }
.draw-panel, .pity-panel { flex: 1; padding: 20px; border: 1px solid #e8eaec; border-radius: 6px; }
.scope-group { margin: 16px 0 22px; }
.material-box { display: flex; align-items: center; gap: 14px; padding: 16px; margin-bottom: 20px; background: #fff8ee; border: 1px solid #ffe1b8; border-radius: 6px; }
.material-icon { color: #801dae; font-size: 32px; }
.material-box p { margin: 6px 0 0; color: #808695; }
.empty-hint { color: #ed4014; text-align: center; }
.pity-row { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #f0f0f0; }
.pity-row strong { color: #ff6b35; }
.scope-note { color: #808695; line-height: 1.7; }
.history-item { padding: 14px 0; border-bottom: 1px solid #e8eaec; }
.history-time { margin-left: 12px; color: #9ea7b4; font-size: 12px; }
.history-summary { margin-top: 8px; color: #808695; }
.history-summary .ivu-tag { margin-left: 8px; margin-top: 4px; }
.empty-history { padding: 30px; color: #9ea7b4; text-align: center; }
.reveal-stage { position: relative; min-height: 420px; overflow: hidden; padding: 4px; background: #111522; border-radius: 6px; color: #eef2f7; }
.rare-flash { position: absolute; z-index: 8; inset: 0; pointer-events: none; animation: rare-screen-flash .7s ease-out both; }
.flash-quality-2 .rare-flash { background: radial-gradient(circle, rgba(175, 98, 255, .58), rgba(93, 45, 145, .08) 55%, transparent 75%); }
.flash-quality-3 .rare-flash { background: radial-gradient(circle, rgba(255, 225, 117, .92), rgba(205, 143, 26, .2) 50%, transparent 78%); }
.charging-scene { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; background: radial-gradient(circle at 50% 55%, #532827 0, #1c1b29 34%, #10131d 72%); }
.charging-scene strong { z-index: 2; margin-top: 24px; font-size: 24px; font-weight: 600; }
.charging-scene > span { z-index: 2; margin-top: 7px; color: #bfc5d2; }
.hearth-core { z-index: 2; display: flex; width: 84px; height: 84px; align-items: center; justify-content: center; border-radius: 50%; background: #351b22; color: #ffb04b; font-size: 48px; box-shadow: 0 0 28px rgba(255, 93, 44, .72), inset 0 0 20px rgba(255, 196, 78, .28); animation: hearth-pulse .85s ease-in-out infinite alternate; }
.hearth-rings, .hearth-rings i { position: absolute; width: 190px; height: 190px; border: 1px solid rgba(255, 157, 71, .38); border-radius: 50%; }
.hearth-rings { animation: hearth-spin 5s linear infinite; }
.hearth-rings i:nth-child(1) { transform: rotateX(64deg); }
.hearth-rings i:nth-child(2) { transform: rotateY(64deg); }
.hearth-rings i:nth-child(3) { width: 135px; height: 135px; margin: 27px; border-style: dashed; animation: hearth-spin 3s linear infinite reverse; }
.result-head { display: flex; justify-content: space-between; padding: 12px 14px 10px; color: #aeb8ca; }
.result-head strong { color: #ffca6a; }
.result-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; padding: 6px 12px 14px; perspective: 1200px; }
.result-card-shell { height: 160px; perspective: 900px; }
.result-card { position: relative; width: 100%; height: 100%; transform-style: preserve-3d; transition: transform .58s cubic-bezier(.2, .75, .22, 1); }
.result-card-shell.revealed .result-card { transform: rotateY(180deg); }
.result-card-back, .result-card-front { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; overflow: hidden; border: 1px solid #50596b; border-radius: 5px; backface-visibility: hidden; }
.result-card-back { gap: 7px; background: linear-gradient(145deg, #252c3b, #171b27); color: #dca65b; box-shadow: inset 0 0 0 4px #1b202d, inset 0 0 0 5px rgba(220, 166, 91, .35); }
.result-card-back .ivu-icon { font-size: 34px; }
.result-card-back span { font-size: 12px; }
.result-card-front { gap: 6px; padding: 10px 5px; transform: rotateY(180deg); background: #202838; text-align: center; }
.result-card-front > *:not(.quality-aura) { position: relative; z-index: 2; }
.result-card-front strong { width: 100%; overflow: hidden; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }
.result-card-front span, .result-card-front small { color: #bdc7d8; font-size: 11px; }
.quality-aura { position: absolute; inset: 0; opacity: .75; }
.quality-1 { border-color: #468ad1; box-shadow: 0 0 14px rgba(49, 137, 225, .28); }
.quality-1 .quality-aura { background: radial-gradient(circle at 50% 20%, rgba(57, 145, 230, .34), transparent 65%); }
.quality-2 { border-color: #b269ff; box-shadow: 0 0 22px rgba(167, 84, 255, .66); animation: rare-card-purple 1.3s ease-in-out infinite alternate; }
.quality-2 .quality-aura { background: radial-gradient(circle at 50% 20%, rgba(178, 105, 255, .58), transparent 68%); }
.quality-3 { border-color: #ffd66b; box-shadow: 0 0 28px rgba(255, 204, 69, .85); animation: rare-card-gold 1s ease-in-out infinite alternate; }
.quality-3 .quality-aura { background: radial-gradient(circle at 50% 18%, rgba(255, 218, 92, .82), rgba(154, 93, 20, .15) 50%, transparent 72%); }
.result-complete { padding: 2px 12px 14px; color: #d7deea; text-align: center; animation: result-complete-in .35s ease-out both; }
.result-complete .ivu-icon { margin-right: 5px; color: #5fcf8e; font-size: 18px; }
.result-footer { min-height: 32px; }
@keyframes hearth-pulse { from { transform: scale(.93); filter: brightness(.9); } to { transform: scale(1.08); filter: brightness(1.2); } }
@keyframes hearth-spin { to { transform: rotate(360deg); } }
@keyframes rare-screen-flash { 0% { opacity: 0; } 24% { opacity: 1; } 100% { opacity: 0; } }
@keyframes rare-card-purple { from { box-shadow: 0 0 15px rgba(167, 84, 255, .42); } to { box-shadow: 0 0 29px rgba(190, 115, 255, .88); } }
@keyframes rare-card-gold { from { box-shadow: 0 0 18px rgba(255, 204, 69, .52); } to { box-shadow: 0 0 34px rgba(255, 222, 105, 1); } }
@keyframes result-complete-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
@media (max-width: 700px) {
  .activity-card { margin: 8px; }
  .draw-layout { flex-direction: column; gap: 14px; }
  .reveal-stage { min-height: 510px; }
  .result-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; }
  .result-card-shell { height: 78px; }
  .result-card-front { gap: 2px; padding: 5px 3px; }
  .result-card-front strong { font-size: 12px; }
  .result-card-front small { display: none; }
  .result-head { font-size: 12px; }
}
@media (prefers-reduced-motion: reduce) {
  .hearth-core, .hearth-rings, .hearth-rings i, .quality-2, .quality-3, .rare-flash, .result-complete { animation: none !important; }
  .result-card { transition: none; }
}
</style>



// WEBPACK FOOTER //
// src/components/hearthActivity.vue